<template>
  <div class="map">
    <GmapMap ref="mapRef" :center="center" :zoom="7" :options="mapOptions" @click="setMarker">
      <GmapMarker key="location" :position="location" :draggable="true" @dragend="setMarkerDragged"></GmapMarker>
      <div class="geolocation" ref="geolocation" @click="getMyLocation()">
        <img src="/static/img/icons/geolocation.svg" class="geolocation__icon" aria-label="Geolocation" alt="geolocation"/>
      </div>
    </GmapMap>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LocationService from '@/services/LocationService';
import { clone } from 'lodash';
import properties from '@/properties';
import { EventBus } from '@/services/EventBus';
import { DROP } from '@/enum/map-stat-types.enum';

const SERVER_URL = properties.SERVER_URL;
const IRELAND_CENTER = {
  lat: 53.3498053,
  lng: -8.260309699999993
};
const PLACE_ZOOM_LEVEL = 16;

export default {
  data() {
    return {
      center: clone(IRELAND_CENTER),
      mapOptions: null,
      map: null,
      imageOverlay: null,
      lastValidCenter: null,
      overlay: null
    }
  },
  computed: {
    ...mapGetters('map', ['location', 'globalBoundingBox', 'networkName',
      'technologyName'])
  },
  methods: {
    ...mapActions('map', ['saveLocation', 'sendMapStat', 'saveBounds',
      'saveZoom']),

    async initializePlace (geocoder) {
      const {place_id: placeId, location: locStr} = this.$route.query;
      let location = LocationService.parseLocation(locStr);

      if (placeId || location) {
        let geocoderOptions = placeId ? { placeId } : { location };
        await this.geocodeByParams(geocoder, geocoderOptions);
      } else {
        this.getMyLocation()
      }
    },
    async geocodeByParams(geocoder, geocoderOptions, config = {
      zoom: true,
      center: true
    }) {
      return new Promise((resolve) => {
        let hasResult;
        geocoder.geocode(geocoderOptions, (results, status) => {
          if (status === 'OK') {
            if (results.length) {
              let resultLoc = LocationService.transformGeocoded(results[0]);
              this.saveLocation(resultLoc);
              if (config.zoom) this.zoomInPlace(true);
              if (config.center) this.centerMap();

              hasResult = true
            } else {
              console.log('No results found');
            }
          } else {
            console.log('Geocoder failed due to: ' + status);
          }
          resolve(hasResult)
        });
      });
    },

    getMyLocation () {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async position => {
          let pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.center = pos;

          const isResultFound = await this.geocodeByParams(new google.maps.Geocoder(), { location: pos });

          if (!isResultFound) {
            this.saveLocation(pos);
            this.zoomInPlace(true);
            this.centerMap();
          }
        });
      }
    },

    async setMarker (location) {
      const isResultFound = await this.geocodeByParams(new google.maps.Geocoder(), { location: location.latLng });

      if (!isResultFound) {
        this.saveLocation(location.latLng);
        this.zoomInPlace();
        this.centerMap();
      }
      this.sendDropPinStats();
    },

    async setMarkerDragged (location) {
      const isResultFound = await this.geocodeByParams(new google.maps.Geocoder(), { location: location.latLng }, {});
      if (!isResultFound) this.saveLocation(location.latLng);

      if (this.map.getZoom() < PLACE_ZOOM_LEVEL) {
        this.centerMap();
      }

      this.zoomInPlace();
      this.sendDropPinStats();
    },

    overlayUpdate () {
      this.saveBounds(this.map.getBounds()); // TODO: refactor this
      this.saveZoom(this.map.getZoom()); // TODO: this too
      this.overlay.setBounds(this.map.getBounds());
      this.overlay.setZoom(this.map.getZoom());
      this.overlay.setTechnology(this.technologyName);
      this.overlay.setOperator(this.networkName);
      this.overlay.draw();
    },

    doNotLetGoOutsideIreland () {
      if (!this.globalBoundingBox) return; // controll Ireland bounds
      let strictBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(this.globalBoundingBox.minLat,
          this.globalBoundingBox.minLon),
        new google.maps.LatLng(this.globalBoundingBox.maxLat,
          this.globalBoundingBox.maxLon)
      );
      if (strictBounds && strictBounds.contains(this.map.getCenter())) {
        this.lastValidCenter = this.map.getCenter();
      } else this.map.panTo(this.lastValidCenter); // not valid anymore => return to last valid position
    },

    zoomInPlace (force) {
      if (this.map.getZoom() > PLACE_ZOOM_LEVEL && !force) return;
      this.map.setZoom(PLACE_ZOOM_LEVEL);
    },

    centerMap () {
      if (LocationService.isValidLatLng(this.location)) {
        this.map.panTo(clone(this.location));
      }
    },

    sendDropPinStats () {
      this.sendMapStat({
        technology: this.technologyName,
        operator: this.networkName,
        lat: this.location && this.location.lat,
        lon: this.location && this.location.lng,
        address: this.location && this.location.address,
        placeId: this.location && this.location.placeId,
        type: DROP
      });
    }
  },
  watch: {
    networkName: function () {
      if (this.overlay) this.overlayUpdate();
    },
    technologyName: function () {
      if (this.overlay) this.overlayUpdate();
    }
  },
  mounted () {
    this.$refs.mapRef.$mapPromise.then((map) => {
      this.mapOptions = { // dynamically loaded options to change positions of buttons, zoom, etc.
        mapTypeControl: true,
        mapTypeControlOptions: {
          position: google.maps.ControlPosition.TOP_RIGHT
        },
        streetViewControl: false,
        minZoom: 6,
        maxZoom: 20,
        gestureHandling: 'cooperative'
      };
      this.map = map; // save direct link to map to the scope
      map.controls[google.maps.ControlPosition.RIGHT_BOTTOM]
        .push(this.$refs.geolocation); // places geolocation button on map

      let geocoder = new google.maps.Geocoder(); // check place_id and select location
      this.initializePlace(geocoder);

      var coverageLevelModule = require('@/services/CoverageOverlay.js');
      let { CoverageOverlay } = coverageLevelModule; // custom overlay for google maps representing coverage

      var srcImage = SERVER_URL + '/api/coverage';
      this.overlay = new CoverageOverlay(map, srcImage, this.networkName,
        this.technologyName);

      this.lastValidCenter = map.getCenter(); // for controlling Ireland bounds

      map.addListener('idle', this.overlayUpdate);
      // map.addListener('bounds_changed', this.doNotLetGoOutsideIreland);
      // map.addListener('center_changed', this.overlayUpdate);
      EventBus.$on('google_place_selected', () => {
        this.zoomInPlace(true);
        this.centerMap();
      })
    });
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
@import 'nib'

.map
  height 100%

.vue-map-container
  height 100%

.geolocation
  row()
  square(40px)
  notSelectable()
  clickable()
  justify-content center
  align-items center
  animation none !important
  background white
  margin 0 10px
  border-radius 2px
  box-shadow 0 1px 4px -1px rgba(0, 0, 0, 0.3)
  box-sizing border-box
  overflow hidden

  &__icon
    square($iconSize = 18px)
    opacity 0.9
    background-size $iconSize

  &:hover &__icon
    opacity 1

</style>
